import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import Layout from "./components/Layout";
import FloatingWhatsApp from "./components/FloatingWhatsApp";
import FloatingCall from "./components/FloatingCall";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";

// Pages
import Maintenance from "./pages/Maintenance";
import Home from "./pages/Home";
import Apply from "./pages/Apply";
import About from "./pages/About";
import Activities from "./pages/Activities";
import ActivitiesFeed from "./pages/ActivitiesFeed";
import ActivitiesEvents from "./pages/ActivitiesEvents";
import ActivitiesGallery from "./pages/ActivitiesGallery";
import EventRegistration from "./pages/EventRegistration";
import Team from "./pages/Team";
import OswalSabha from "./pages/OswalSabha";
import Contact from "./pages/Contact";
import Directory from "./pages/Directory";
import Membership from "./pages/Membership";
import MockCheckout from "./pages/MockCheckout";
import ApplicationPrint from "./pages/ApplicationPrint";
import Success from "./pages/Success";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsConditions from "./pages/TermsConditions";
import RefundPolicy from "./pages/RefundPolicy";
import Disclaimer from "./pages/Disclaimer";

import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";

import MemberLogin from "./pages/MemberLogin";
import MemberVerify from "./pages/MemberVerify";
import MemberDashboard from "./pages/MemberDashboard";
import MemberProfile from "./pages/MemberProfile";
import MemberWelfare from "./pages/MemberWelfare";

const MAINTENANCE_MODE = false;

const pub = (Page) => (
  <Layout>
    <Page />
  </Layout>
);

function ScrollToTop() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname, search]);

  return null;
}

function AppRoutes() {
  return (
    <Routes>
      {/* ========================= */}
      {/* PUBLIC ROUTES */}
      {/* ========================= */}
      <Route path="/" element={pub(Home)} />
      <Route path="/about" element={pub(About)} />
      <Route path="/apply" element={<Apply />} />
      <Route path="/activities" element={pub(Activities)} />
      <Route path="/activities/feed" element={pub(ActivitiesFeed)} />
      <Route path="/activities/events" element={pub(ActivitiesEvents)} />
      <Route path="/activities/gallery" element={pub(ActivitiesGallery)} />
      <Route path="/events/new-event/register" element={<EventRegistration />} />
      <Route path="/team" element={pub(Team)} />
      <Route path="/oswal-sabha" element={pub(OswalSabha)} />
      <Route path="/directory" element={pub(Directory)} />
      <Route path="/contact" element={pub(Contact)} />
      <Route path="/membership" element={pub(Membership)} />
      <Route path="/checkout/:id" element={<MockCheckout />} />
      <Route path="/membership/print/:id" element={<ApplicationPrint />} />
      <Route path="/success/:id" element={pub(Success)} />
      <Route path="/privacy-policy" element={pub(PrivacyPolicy)} />
      <Route path="/terms-conditions" element={pub(TermsConditions)} />
      <Route path="/refund-policy" element={pub(RefundPolicy)} />
      <Route path="/disclaimer" element={pub(Disclaimer)} />

      {/* ========================= */}
      {/* ADMIN */}
      {/* ========================= */}
      <Route path="/admin/login" element={<AdminLogin />} />

      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute role="admin">
            <AdminDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin"
        element={<Navigate to="/admin/dashboard" replace />}
      />

      {/* ========================= */}
      {/* MEMBER AUTH */}
      {/* ========================= */}
      <Route path="/member/login" element={<MemberLogin />} />
      <Route path="/member/verify" element={<MemberVerify />} />

      {/* ========================= */}
      {/* MEMBER PROTECTED */}
      {/* ========================= */}
      <Route
        path="/member/dashboard"
        element={
          <ProtectedRoute role="member">
            <MemberDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/member/profile"
        element={
          <ProtectedRoute role="member">
            <MemberProfile />
          </ProtectedRoute>
        }
      />

      <Route
        path="/member/welfare"
        element={
          <ProtectedRoute role="member">
            <MemberWelfare />
          </ProtectedRoute>
        }
      />

      <Route
        path="/member"
        element={<Navigate to="/member/dashboard" replace />}
      />

      {/* ========================= */}
      {/* 404 */}
      {/* ========================= */}
      <Route
        path="*"
        element={
          <div className="min-h-screen flex items-center justify-center text-xl">
            Page Not Found
          </div>
        }
      />
    </Routes>
  );
}

function App() {
  if (MAINTENANCE_MODE) {
    return <Maintenance />;
  }

  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="App">
          <ScrollToTop />
          <Toaster position="top-right" richColors closeButton />
          <AppRoutes />
          <FloatingCall />
          <FloatingWhatsApp />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
